
import { mergeMap } from 'rxjs/operators';
import {
    Component,
    Input,
    ContentChild,
    TemplateRef,
    AfterContentInit,
    Renderer2,
    OnDestroy,
    ViewChild,
    ElementRef,
    Output,
    EventEmitter,
    AfterViewInit
} from '@angular/core';
import { TabComponent } from '../Tab/tab.component';
import { TabsComponent, TabsOrientationsEnum } from '../Tabs/tabs.component';
import { ScrollPanelComponent } from '../../../Basic/components/ScrollPanel/scroll-panel.component';
import { Observable, Subject, of, throwError, Subscription } from 'rxjs';
import { trigger, style, transition, animate, keyframes, AnimationEvent, state } from '@angular/animations';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';

@Component({
    selector: 'agpr-right-sidebar',
    templateUrl: './right-sidebar.component.html',
    styleUrls: ['./right-sidebar.component.scss'],
    animations: [
        trigger('slide', [
            transition('* => expanded', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        width: '{{containerWidth}}px',
                        position: 'fixed',
                        right: 0
                    }),
                    style({
                        width: '100vw',
                        position: 'fixed',
                        right: 0
                    })
                ]))
            ], { params: { containerWidth: 300 } }),
            transition('expanded => *', [
                animate('300ms ease-in-out', keyframes([
                    style({
                        width: '100vw',
                        position: 'fixed',
                        right: 0,
                        offset: 0
                    }),
                    style({
                        width: '{{containerWidth}}px',
                        offset: 1
                    }),
                    style({
                        position: 'unset',
                        right: 'unset',
                        offset: 1
                    }),
                ]))
            ], { params: { containerWidth: 300 } })
        ]),
        trigger('fade', [
            transition('* => show', [
                animate('150ms ease-in-out', keyframes([
                    style({
                        opacity: 0
                    }),
                    style({
                        opacity: 1
                    })
                ]))
            ]),
            transition('show => *', [
                animate('150ms ease-in-out', keyframes([
                    style({
                        opacity: 1
                    }),
                    style({
                        opacity: 0
                    })
                ]))
            ])
        ]),
        trigger('contentCollapse', [
            state(
                'expanded',
                style({
                    width: '{{width}}',
                    minWidth: '{{minWidth}}'
                }),
                {params: {width: '100%', minWidth: '350px'}}
            ),
            state(
                'collapsed',
                style({
                    width: '40px',
                    minWidth: '40px'
                }),
            ),
            transition('* <=> *', [animate('0.5s ease-in-out')])
        ])
    ],
    standalone: false
})
export class RightSidebarComponent extends TabsComponent implements AfterContentInit, OnDestroy, AfterViewInit {
    @Input() id: string;
    @Input() set collapsible(collapsible: boolean) {
        this._collapsible = collapsible;
        if (!this._collapsible) {
            this.selectTabByIndex(this.initialTabIndex);
        } else {
            this.unselectTab();
        }
    }
    @Input() contentCollapseToggle: boolean = false;
    @Input() contentCollapseParams: {width: string; minWidth: string};
    @Input() overlayContainer: Element;
    @Input() set expanded(value: boolean) {
        if (value !== this._expanded) {
            if (value) {
                this.expand();
            } else {
                this.collapse();
            }
        }
    }
    @Input() contentWidth: string;
    // wait for content initialization
    @Input() waitForContentInit: boolean = false;
    @Input() showTabTitle: boolean = true;

    @Input() set selectedTabIndex(value: number) {
        if (!this.waitForContentInit && !this.contentInitialized) {
            this.initialTabIndex = value;
            return;
        }

        if (value !== this._selectedTabIndex) {
            this.selectTabByIndex(value, true, false);
        }
    }

    @Input() loading: boolean;
    @Input() useWhiteBg: boolean = false;
    @Input() headerIcon: string;
    @Input() headerIconTooltip: string;

    @Output() headerIconClicked: EventEmitter<void> = new EventEmitter<void>();

    @Output() expandedChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() unfocusEmit: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ContentChild('content') public content: TemplateRef<any>;
    @ContentChild('header') public header: TemplateRef<any>;
    @ContentChild('footer') public footer: TemplateRef<any>;

    @ViewChild('scrollPanel') public contentScrollPanel: ScrollPanelComponent;
    @ViewChild('container') public container: ElementRef;
    @ViewChild('contentContainer') public contentContainer: ElementRef;

    _collapsible: boolean = false;
    _expanded: boolean = false;
    slideAnimation: {value: 'collapsed'|'expanded'; params: {containerWidth: number}};
    contentCollapseAnimation: {
        value: 'collapsed'|'expanded';
        params: {
            width: string;
            minWidth: string;
        };
    };
    private slideAnimation$: Subject<AnimationEvent>;
    public showExtendedContent: boolean;
    public fadeAnimation: 'hide'|'show';
    private fadeAnimation$: Subject<AnimationEvent>;
    contentCollapsed: boolean = false;

    public overlay: Element;
    private previousTabIndex: number = 0;

    selectedTab: TabComponent;

    tabFooter: TemplateRef<any>;
    tabExtended: TemplateRef<any>;

    subscriptions: Subscription[] = [];


    clickOutsideExcluded: string = '.agpr-clickOutsideExcluded';
    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        protected renderer: Renderer2,
        protected el: ElementRef
    ) {
        super(el);
    }

    ngAfterContentInit(): void {
        if (this._collapsible && this._selectedTabIndex === null) {
            return;
        }
        super.ngAfterContentInit();
        this.subscriptions.push(
            this.tabs.changes.subscribe(change => {
                requestAnimationFrame(() => {
                    this.removeFocus();
                    const tabs = this.tabs.toArray();
                    const tab = tabs[this._selectedTabIndex];
                    if (tab) {
                        this.addFocus(tab, false);
                    } else {
                        // The tab that was selected is no longer present, move selection to top tab
                        // TODO: Create a system for 'defaultTab' so that we can select the default tab for the view instead of the first tab
                        this.selectTab(tabs[0], true, true);
                    }
                });
            })
        );
    }

    ngAfterViewInit(): boolean {
        if (super.ngAfterViewInit()) {
            return true;
        }
        const tab = this.tabs.toArray()[this._selectedTabIndex];

        requestAnimationFrame(() => {
            // re-select the tab if the view initialized a header
            // has to be a separate frame to make sure it works outside of dev mode
            if (tab && (this.tabFooter !== tab.footer || this.tabExtended !== tab.extendedContent)) {
                this.selectTab(tab, false);
                this.addFocus(tab, false);
            }
            this.orientation = TabsOrientationsEnum.VERTICAL;
        });

        return false;
    }

    ngOnDestroy(): void {
        this.hideOverlay();
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    getIconClasses(tab: any): string {
        const base = 'agpr-rightSidebar-tab-icon ';
        const baseClass = base + tab.icon;
        const iconClassMap: { [key: string]: string } = {
            'fa-kit-duotone fa-agpr-outlet': 'fa-agpr-outlet-solid fa-kit',
            'fa-kit-duotone fa-agpr-lists': 'fa-agpr-lists-solid fa-kit',
            'fa-kit-duotone fa-agpr-engage': 'fa-kit fa-agpr-engage-solid',
            'fa-kit-duotone fa-agpr-insights': 'fa-kit fa-agpr-insights-solid',
            'fa-kit-duotone fa-agpr-notes': 'fa-kit fa-agpr-notes-solid'
        };

        if (tab.icon.includes('agpr')) {
            if (iconClassMap[tab.icon]) {
                return tab.selected ? base + ' ' + iconClassMap[tab.icon] : baseClass;
            } else {
                return baseClass;
            }
        } else {
            const iconClass = tab.selected ? 'fa-solid' : 'fa-regular fa-duotone';
            return baseClass + ' ' + iconClass;
        }
    }

    selectPreviousVisibleTab(): void {
        if (!this.tabs) {
            return;
        }
        if (this._collapsible) {
            this.unselectTab();
            return;
        }

        // attempt to select previous tab
        const previousTab = this.tabs.toArray()[this.previousTabIndex];
        if (!previousTab?.hidden) {
            this.selectTab(previousTab);
            return;
        }
        // select first tab that isn't hidden
        for (const tab of this.tabs) {
            if (tab && !tab.hidden) {
                this.selectTab(tab);
                return;
            }
        }
    }

    selectTabByIndex(index: number, emitChanges: boolean = true, moveFocus: boolean = true): void {
        if (!this.tabs) {
            return;
        }
        if (this._collapsible && index === null) {
            this.unselectTab();
            return;
        }
        this.selectTab(this.tabs.toArray()[index] || this.tabs.first, emitChanges, moveFocus);
    }

    selectTab(selectedTab: TabComponent, emitChanges: boolean = true, moveFocus: boolean = true): void {
        super.selectTab(selectedTab, emitChanges, moveFocus);

        // Parent already dealt with this case
        if (!selectedTab || selectedTab.disabled) {
            return;
        }

        this.tabFooter = selectedTab.footer;
        this.tabExtended = selectedTab.extendedContent;
        this.selectedTab = selectedTab;
        this.headerIcon = selectedTab.headerIcon;
        this.headerIconTooltip = selectedTab.headerIconTooltip;

        if (this._expanded && !this.tabExtended) {
            this.collapse();
        }

        if (this._collapsible) {
            this.showOverlay();
        }
    }

    /**
     * Used when the elements found will have a nativeElement property. Ie., when each
     * element is a 'wrapper around a native element inside of a View'.
     * For use with scrollFirstIntoView(elements)
     * @param errorClass The class string to search within the sidebar
     * @returns An array containing all the elements found
     */
    getErrorElements(errorClass: string): ElementRef[] {
        const errorList = this.container.nativeElement.querySelectorAll(errorClass);
        return Array.from(errorList);
    }

    /**
     * Used when the elements found will have not a nativeElement property. Ie., when each
     * element is a direct HTMLElement, not an element inside a View.
     * For use with scrollFirstIntoViewNative(elements)
     * @param errorClass The class string to search within the sidebar
     * @returns An array containing all the native elements found
     */
    getErrorElementsNative(errorClass: string): HTMLElement[] {
        const errorList = this.container.nativeElement.querySelectorAll(errorClass);
        return Array.from(errorList);
    }

    public scrollToFirstElement(elements: ElementRef[]): void {
        this.contentScrollPanel.scrollFirstIntoView(elements);
    }

    public scrollToFirstElementNative(elements: HTMLElement[]): void {
        this.contentScrollPanel.scrollFirstIntoViewNative(elements);
    }

    public unselectTab(): void {
        super.unselectTab();

        this.hideOverlay();

        this.tabFooter = null;
    }

    public slideAnimationComplete(event: AnimationEvent): void {
        if (this.slideAnimation$) {
            this.slideAnimation$.next(event);
            this.slideAnimation$.complete();
            this.slideAnimation$ = null;
        }
    }
    public fadeAnimationComplete(event: AnimationEvent): void {
        if (this.fadeAnimation$) {
            this.fadeAnimation$.next(event);
            this.fadeAnimation$.complete();
            this.fadeAnimation$ = null;
        }
    }

    private startSlideAnimation(expanded: boolean): Observable<AnimationEvent> {
        this.slideAnimation = {
            value: expanded ? 'expanded' : 'collapsed',
            params: {
                containerWidth: this.container.nativeElement.parentElement.offsetWidth
            }
        };
        this._expanded = expanded;
        this.slideAnimation$ = new Subject<AnimationEvent>();

        return this.slideAnimation$;
    }
    private startFadeAnimation(expanded: boolean): Observable<AnimationEvent> {
        this.fadeAnimation = expanded ? 'show' : 'hide';
        this.fadeAnimation$ = new Subject<AnimationEvent>();

        if (expanded) {
            this.showExtendedContent = true;
        } else {
            this.fadeAnimation$.subscribe(() => this.showExtendedContent = false);
        }

        return this.fadeAnimation$;
    }
    public cancelExistingAnimations(): void {
        if (this.slideAnimation$) {
            this.slideAnimation$.error(null);
            this.slideAnimation$ = null;
        }
        if (this.fadeAnimation$) {
            this.fadeAnimation$.error(null);
            this.fadeAnimation$ = null;
        }
    }
    public expand(): Observable<AnimationEvent> {
        this.cancelExistingAnimations();
        if (!this.tabExtended) {
            if (this._expanded) {
                this.collapse();
            }
            return throwError(null);
        }
        const animations = this.startSlideAnimation(true).pipe(
            mergeMap(() => this.startFadeAnimation(true)));
        animations.subscribe(() => {
            this.expandedChange.emit(true);
        });
        return animations;
    }
    public collapse(): Observable<AnimationEvent> {
        this.cancelExistingAnimations();
        const animations = this.startFadeAnimation(false).pipe(
            mergeMap(() => this.startSlideAnimation(false)));
        animations.subscribe(() => {
            this.expandedChange.emit(false);
        } );
        return animations;
    }
    public toggleExpand(): Observable<AnimationEvent> {
        if (this._expanded) {
            return this.collapse();
        }
        return this.expand();
    }

    public scrollToTop(): void {
        this.scrollPanel?.scrollTo(0, 0, false);
    }

    clickOutside(event): void {
        if (!document.body.contains(event.target) || event.target === document.body) {
            return;
        }

        this.closeFocus();
    }

    closeFocus(event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        let selectedTab = this._selectedTabIndex;
        if (this._collapsible) {
            this.unselectTab();
            this.expandedChange.emit(false);
        } else {
            this.unfocus();
            this.unfocusEmit.emit(false);
        }

        // Closes any panels that are open before toggling
        document.body.click();

        // move the focus back
        if (!selectedTab) {
            selectedTab = 0;
        }
        this.addFocus(this.tabs.toArray()[selectedTab]);
    }

    focus(): void {
        this.previousTabIndex = this._selectedTabIndex;
        this.showOverlay();
    }
    unfocus(): void {
        this.hideOverlay();
    }

    showOverlay(): void {
        if (this.overlay || !this.overlayContainer) {
            return;
        }
        this.overlay = this.renderer.createElement('div');
        this.overlay.classList.add('agpr-rightSidebar-overlay');
        this.overlayContainer.appendChild(this.overlay);
    }

    hideOverlay(): void {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
    }

    toggleContent() {
        this.contentCollapsed = !this.contentCollapsed;
        this.contentCollapseAnimation = {
            value: this.contentCollapsed ? 'collapsed' : 'expanded',
            params: {
                width: this.contentCollapseParams.width,
                minWidth: this.contentCollapseParams.width
            }
        };

    }

    contentCollapseComplete(event) {
        if (this.contentCollapseToggle) {
            this.contentContainer.nativeElement.style.pointerEvents = event.toState === 'collapsed' ? 'none' : 'auto';
        }
    }
}
